import { Dispatch, SetStateAction, useEffect } from "react";
import { useMe } from "@/data/user.ts";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { AllTutorsHourObj } from "@/pages/OneToOne";
import { availableCredits } from "@/utils/one-to-one.ts";
import { useOneToOneCredits } from "@/data/one-to-one.ts";
import { Trans, useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import Button from "@edusogno/primitives/button";
import Warning from "@/components/ui/Warning.tsx";
import timezone from "dayjs/plugin/timezone";
import tutorPic from "@/assets/img/tutorPic.png";
import HourPicker from "@edusogno/composites/hours";
import useOneToOne from "@/utils/hooks/useOneToOne";
import InfoOutlineIcon from "@/components/icons/InfoOutlineIcon.tsx";
import CustomDatePicker from "@edusogno/composites/calendar";
import OneToOneHourSelect from "@/components/ui/OneToOneHourSelect.tsx";

const CHECKOUT_BASE = import.meta.env.EDS_CHECKOUT_BASE;

dayjs.extend(timezone);

interface IProps {
  isLoading: boolean;
  setSecondSession: Dispatch<SetStateAction<boolean>>;
  selectedDayEvents: AllTutorsHourObj[];
  previousTutorInfo?: { name: string; image: string; token: string };
  availableCalendarDays: Dayjs[];
  selectedPreviousTutorDayEvents: string[];
}

const DesktopView = ({
  isLoading,
  setSecondSession,
  selectedDayEvents,
  previousTutorInfo,
  availableCalendarDays,
  selectedPreviousTutorDayEvents,
}: IProps) => {
  const { me } = useMe();
  const { t, i18n } = useTranslation();
  const { credits } = useOneToOneCredits();
  const { openModal } = useModalAction();

  const {
    date,
    hour,
    tutor,
    credits: stateCredits,
    setState,
    selectedPreviousTutor,
    isFirstSession,
  } = useOneToOne();

  useEffect(() => {
    if (!credits) return;
    setState("credits", credits.item.count_credits);
    setState("isFirstSession", credits.item.is_first_one_to_one);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credits]);

  const handleAnimationInjection = () => {
    const element = document.getElementById("creditsDiv");

    if (element) {
      element.classList.add("animate-bounce");

      setTimeout(() => {
        element.classList.remove("animate-bounce");
      }, 5000);
    }
  };

  if (isLoading) return <></>;

  return (
    <div className="w-full h-[--content-height] flex gap-4">
      <div className="h-full w-full bg-white rounded-md">
        <div className="w-full h-full flex-col justify-between items-center flex bg-white rounded-md py-3 2xl:py-8 px-6">
          <div className="flex flex-col gap-1 w-full mb-4">
            <h2 className="text-xl font-semibold text-secondary">
              {t("one_to_one.desktop_title_section")}
            </h2>

            <p className="text-base text-secondary">
              {t("one_to_one.desktop_subtitle_section")}
            </p>
          </div>

          <>
            <div className="grid grid-cols-2 h-full w-full gap-8 2xl:max-w-[1250px] xl:mx-auto 3xl:min-h-[50cqh] min-h-[60%] justify-center">
              <div className="flex-s h-1/2 2xl:h-full justify-center w-full gap-x-4 mx-auto">
                <CustomDatePicker
                  lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
                  type="single"
                  value={dayjs(date).format("YYYY-MM-DD")}
                  minDate={dayjs()}
                  openDates={availableCalendarDays}
                  onValueChange={(d) => setState("date", d as string)}
                />
              </div>

              {isFirstSession || !previousTutorInfo ? (
                <div className="w-full flex-[1_1_50%] h-full overflow-x-hidden overflow-y-auto">
                  <HourPicker
                    value={hour as string}
                    hours={selectedDayEvents?.map((el) => el.hour)}
                    openHours={selectedDayEvents?.map((el) => el.hour)}
                    onValueChange={(e) => {
                      setState("hour", e as string);

                      const selectedDay = selectedDayEvents?.find(
                        (el) => el.hour === e,
                      );

                      if (selectedDay) {
                        setState("tutor", selectedDay.tutorsAvailable[0].token);
                      }
                    }}
                  />
                </div>
              ) : (
                <OneToOneHourSelect
                  value={hour as string}
                  hours={selectedPreviousTutorDayEvents}
                  allList={selectedDayEvents}
                  openHours={selectedPreviousTutorDayEvents}
                  isPreviousTutor={selectedPreviousTutor}
                  previousTutorInfo={previousTutorInfo}
                  onValueChange={(time, previousTutor, isPreviousTutor) => {
                    setState("hour", time as string);
                    setState("tutor", previousTutor);

                    const selectedDay = selectedDayEvents?.find(
                      (cal) => cal.hour === time,
                    );

                    if (isPreviousTutor) {
                      setState("selectedPreviousTutor", isPreviousTutor);
                    } else {
                      setState("selectedPreviousTutor", false);
                      if (selectedDay)
                        setState("tutorList", selectedDay.tutorsAvailable);
                    }
                  }}
                />
              )}
            </div>

            <div className="mt-4">
              <Warning
                text={
                  <Trans
                    i18nKey="one_to_one.warning_timezome"
                    components={{
                      b: <b className="font-semibold" />,
                    }}
                  />
                }
              />
            </div>

            <div className="w-full mt-4 flex justify-center">
              <Button
                size="lg"
                disabled={stateCredits === 0 || !date || !hour}
                onClick={() => {
                  if (credits) {
                    if (isFirstSession) {
                      openModal(
                        "CONFIRM_ONE_TO_ONE_FIRST_SESSION_MODAL",
                        { date, hour, tutor },
                        {
                          renderHeader: (
                            <div className="flex items-center px-2 h-10 bg-brand-secondary rounded-t-md">
                              <h2 className="text-sm font-semibold text-left text-light">
                                1:1 classes
                              </h2>
                            </div>
                          ),
                        },
                      );
                    } else if (credits.item.count_credits < 1) {
                      handleAnimationInjection();
                    } else {
                      setSecondSession(true);
                    }
                  }
                }}
                color={isFirstSession ? "green" : "primary"}
              >
                {isFirstSession ? t("btns.confirm") : t("btns.next")}
              </Button>
            </div>
          </>
        </div>
      </div>

      <div className="@container w-full flex-[0_0_320px] flex flex-col gap-2 2xl:justify-between bg-brand-light rounded-md py-4 2xl:py-8 px-2">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col 2xl:justify-evenly h-[40%] w-full items-center gap-4">
            <img
              src={tutorPic}
              alt="tutor-pic"
              className="h-1/6 2xl:h-[46.31px] mx-auto object-contain"
            />

            <div className="flex flex-col gap-1 w-full items-center">
              <h2 className="text-xl font-semibold text-brand text-center">
                {t("one_to_one.how_to_get_credits.title")}
              </h2>

              <p className="text-[12px] font-semibold text-primary">
                {t("one_to_one.how_to_get_credits.subtitle")}
              </p>
            </div>

            <div className="flex flex-row gap-2 items-center mx-auto px-2">
              <InfoOutlineIcon className="w-4 h-4 fill-brand-secondary" />

              <p className="text-14px  font-normal text-brand">
                {t("one_to_one.how_to_get_credits.info")}
              </p>
            </div>
          </div>

          <ScrollArea className="mt-4">
            <div className="grid grid-cols-1 gap-[clamp(12px,1.5cqh,16px)] pb-4">
              {availableCredits.map((r) => (
                <div
                  key={r.label}
                  className="flex flex-row w-full items-center px-5 py-1 bg-light rounded-md border-brand-secondary"
                >
                  <div className="px-1 w-full flex flex-row justify-between items-center text-[clamp(12px,1.5cqh,16px)] gap-2">
                    <p className="text-brand-secondary text-[clamp(12px,1.5cqh,14px)] font-semibold text-start text-sm">
                      {t(r?.label)}
                    </p>

                    <div>
                      <div className="w-full flex justify-center flex-row items-center rounded px-2 py-1 gap-1">
                        <p className="text-sm font-semibold">{r?.price}</p>

                        <p className="text-[10px]">
                          /{t("one_to_one.how_to_get_credits.each")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <Button
          color="primary"
          onClick={() => {
            window.open(
              `${CHECKOUT_BASE}one-to-one/${"adRIlqNoTRGL32CwBF9"}/${me?.item
                .user.token}/academy?quantity=1`,
              "_blank",
            );
          }}
          className="mt-2 !h-[35px]"
        >
          {t("btns.buy_new_credits")}
        </Button>
      </div>
    </div>
  );
};

export default DesktopView;
